import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Lightning } from '@phosphor-icons/react';
import { endOfISOWeek, startOfISOWeek } from 'date-fns';
import { StatisticsAPI } from '@api';
import type { ActivityPattern } from '@api';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';

/**
 * "Vad ger dig energi" på tonåringens Framsteg-sida (beslut B3: ett kort, ingen egen
 * statistiksida). Läser veckans statistik ur samma v2-kontrakt som vuxenvyn och visar
 * bara det som gav energi. Utan tillräckligt underlag säger kortet det i stället för att
 * gissa.
 */
const TeenEnergyCard = ({ cardSx, titleSx }: { cardSx: object; titleSx: object }) => {
  const { t } = useTranslation();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);
  const [activities, setActivities] = useState<ActivityPattern[] | null>(null);
  const [minN, setMinN] = useState(3);

  useEffect(() => {
    let cancelled = false;
    const now = new Date();
    new StatisticsAPI()
      .getOverview('week', startOfISOWeek(now), endOfISOWeek(now))
      .then((overview) => {
        if (cancelled || !overview) return;
        setMinN(overview.sufficiency.minN);
        setActivities(overview.highlights?.givesEnergy ?? []);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Innan svaret finns, eller om det misslyckades, visas inget alls: kortet är ett tillägg,
  // och en tom ruta som blinkar förbi är sämre än ingen ruta.
  if (activities === null) return null;

  return (
    <Box sx={cardSx} component="section">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Lightning size={18} weight="duotone" color={tk.primary} />
        <Typography component="h2" sx={titleSx}>
          {t('teen.progress.energyTitle')}
        </Typography>
      </Stack>
      {activities.length > 0 ? (
        <Stack component="ul" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {activities.map((activity) => (
            <Box component="li" key={activity.activityId}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: tk.text }}>
                {activity.name}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: tk.textSoft }}>
                {t('stats.self.givesEnergyDetail', {
                  times: activity.givingSignals,
                  answered: activity.answered,
                })}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography sx={{ fontSize: '0.85rem', color: tk.textSoft }}>
          {t('stats.self.tooLittleEnergy', { minN })}
        </Typography>
      )}
    </Box>
  );
};

export default TeenEnergyCard;
